<template>
	<PublicPage wide current="/how-it-works" eyebrow="HOW IT WORKS" :title="['From what', 'to done.']" lead="How a Paydeck card payment works, step by step, and the checks that keep it safe along the way.">
		<div class="trust hiw">
			<!-- 01 The steps -->
			<section class="trust-section tone-white" aria-labelledby="hiw-steps">
				<div class="trust-head">
					<div><PdEyebrow>01 / Making a payment</PdEyebrow><h2 id="hiw-steps">Five steps, start to finish.</h2></div>
					<p>A payment takes a few minutes the first time and less after that. You see every detail before any money moves, and you keep a record when it is done.</p>
				</div>

				<ol class="hiw-steps">
					<li v-for="(step, i) in steps" :key="step.title" class="hiw-step">
						<div class="hiw-step-copy">
							<span class="hiw-step-num pd-num">{{ String(i + 1).padStart(2, "0") }}</span>
							<h3>{{ step.title }}</h3>
							<p>{{ step.body }}</p>
							<span class="hiw-step-safe"><ShieldCheck :size="16" :stroke-width="2.25" aria-hidden="true" />{{ step.safe }}</span>
						</div>

						<!-- Preview of the real app screen, built from paydeck-ui components. Not interactive. -->
						<div class="hiw-screen" inert aria-hidden="true">
							<div class="hiw-screen-bar"><span class="journey-app-brand"><img src="/images/paydeck-icon-5.png" alt="" width="20" height="20" />paydeck</span><PdBadge :tone="i === 4 ? 'success' : 'brand'" plain>{{ i === 4 ? "Settled" : `Step ${i + 1} of 5` }}</PdBadge></div>

							<template v-if="i === 0">
								<p class="hiw-screen-title">Sign in to Paydeck</p>
								<PdField label="Mobile number"><PdPhoneInput :model-value="'9830012345'" /></PdField>
								<PdField label="One time password"><PdOtpInput :model-value="'482913'" :resend-after="0" /></PdField>
							</template>

							<template v-else-if="i === 1">
								<p class="hiw-screen-title">What are you paying for?</p>
								<PdRadioCard :model-value="'Rent'" value="Rent" name="hiw-purpose" title="Rent" meta="Your recurring rental payments" :icon="House" />
								<PdField label="Amount to pay"><PdAmountInput :model-value="25000" size="md" readonly :max="999999999" /></PdField>
							</template>

							<template v-else-if="i === 2">
								<p class="hiw-screen-title">Who are you paying?</p>
								<PdRecipientOption :beneficiary="sampleRecipient" :model-value="sampleRecipient.beneficiary_id" name="hiw-recipient" />
								<PdKeyValue :items="recipientItems" />
							</template>

							<template v-else-if="i === 3">
								<p class="hiw-screen-title">Review and pay</p>
								<PdKeyValue :items="reviewItems" />
								<div class="hiw-otp-card">
									<span class="hiw-otp-head"><Landmark :size="16" :stroke-width="2.25" />Your bank</span>
									<p>Enter the OTP sent to your registered mobile to approve this payment.</p>
									<PdOtpInput :model-value="'482913'" :resend-after="0" />
								</div>
							</template>

							<template v-else>
								<div class="journey-app-paid">
									<span class="journey-app-check"><Check :size="26" :stroke-width="3" /></span>
									<div><p class="journey-app-amount pd-num">{{ formatINR(25000) }}</p><p class="journey-app-sub">Paid to {{ sampleRecipient.bank_account_name }}</p></div>
								</div>
								<PdKeyValue :items="receiptItems" />
							</template>
						</div>
					</li>
				</ol>
			</section>

			<!-- 02 The path of a payment -->
			<section class="trust-section tone-ink" aria-labelledby="hiw-path">
				<div class="trust-head">
					<div><PdEyebrow tone="light">02 / Behind the scenes</PdEyebrow><h2 id="hiw-path">Where your payment goes, and who protects it.</h2></div>
					<p>Every payment passes through regulated banks and established payment partners. Each stop adds its own check before the money moves on.</p>
				</div>
				<ol class="hiw-path" aria-label="The path of a payment">
					<li v-for="(node, i) in pathNodes" :key="node.title" class="hiw-node">
						<span class="hiw-node-icon"><component :is="node.icon" :size="26" :stroke-width="2" aria-hidden="true" /></span>
						<strong>{{ node.title }}</strong>
						<span class="hiw-node-check"><Lock :size="13" :stroke-width="2.5" aria-hidden="true" />{{ node.check }}</span>
						<ArrowRight v-if="i < pathNodes.length - 1" class="hiw-node-arrow" :size="22" :stroke-width="2.25" aria-hidden="true" />
					</li>
				</ol>
				<p class="hiw-path-note">Illustrative. The exact banks and partners depend on your card and the recipient's bank.</p>
			</section>

			<!-- 03 Safety -->
			<section class="trust-section tone-cream" aria-labelledby="hiw-safety">
				<div class="trust-head">
					<div><PdEyebrow>03 / Safe by design</PdEyebrow><h2 id="hiw-safety">How we keep every payment safe.</h2></div>
					<p>Safety is not a single feature. It is a set of checks that run before, during and after every payment.</p>
				</div>
				<div class="trust-bento">
					<PdTile v-for="(item, i) in safety" :key="item.title" :tone="item.tone" pad="lg" :class="['hiw-safe-tile', item.span]">
						<span class="fact-icon"><component :is="item.icon" :size="24" :stroke-width="2" aria-hidden="true" /></span>
						<div>
							<h3>{{ item.title }}</h3>
							<p>{{ item.body }}</p>
						</div>
						<span class="hiw-safe-when">{{ item.when }}</span>
					</PdTile>
				</div>
				<PdNotice class="hiw-notice" tone="warning" title="Paydeck will never ask for your OTP, PIN or CVV">
					Not by phone, email, SMS or chat. If anyone asks, it is not us. Contact your bank and read our <a href="/settlement-guide">fraud reporting guidance</a>.
				</PdNotice>
			</section>

			<!-- 04 Start -->
			<section class="trust-section tone-white" aria-labelledby="hiw-start">
				<div class="trust-bento">
					<PdTile tone="ink" pad="lg" rings class="next-tile span-7">
						<PdEyebrow tone="light">Ready when you are</PdEyebrow>
						<h2 id="hiw-start">Make your first payment.</h2>
						<p>Create an account, choose what you are paying for and follow the steps above.</p>
						<div class="next-actions">
							<PdButton :href="registerUrl" :icon-right="ArrowUpRight">Create an account</PdButton>
							<PdArrowLink href="/payment-types" tone="light">See what you can pay</PdArrowLink>
						</div>
					</PdTile>
					<PdTile tone="cream" pad="lg" class="next-tile span-5">
						<PdEyebrow tone="muted">Questions?</PdEyebrow>
						<h2>We are here to help.</h2>
						<p>Read the answers to common questions, or email our team at 121@paydeck.in.</p>
						<div class="next-actions">
							<PdArrowLink href="/frequently-asked-questions" underline>Read the FAQs</PdArrowLink>
						</div>
					</PdTile>
				</div>
			</section>
		</div>
	</PublicPage>
</template>

<script setup>
import { ArrowRight, ArrowUpRight, BadgeCheck, Check, CreditCard, Eye, FileCheck2, House, Landmark, Lock, ReceiptText, ShieldAlert, ShieldCheck, Smartphone, UserCheck, Waypoints } from "lucide-vue-next";
import PdTile from "@paydeck-ui/components/PdTile.vue";
import PdEyebrow from "@paydeck-ui/components/PdEyebrow.vue";
import PdBadge from "@paydeck-ui/components/PdBadge.vue";
import PdField from "@paydeck-ui/components/PdField.vue";
import PdPhoneInput from "@paydeck-ui/components/PdPhoneInput.vue";
import PdOtpInput from "@paydeck-ui/components/PdOtpInput.vue";
import PdRadioCard from "@paydeck-ui/components/PdRadioCard.vue";
import PdAmountInput from "@paydeck-ui/components/PdAmountInput.vue";
import PdKeyValue from "@paydeck-ui/components/PdKeyValue.vue";
import PdNotice from "@paydeck-ui/components/PdNotice.vue";
import PdButton from "@paydeck-ui/components/PdButton.vue";
import PdArrowLink from "@paydeck-ui/components/PdArrowLink.vue";
import PdRecipientOption from "@paydeck-ui/components/payments/PdRecipientOption.vue";
import { formatINR } from "@paydeck-ui/data/format.js";
import PublicPage from "@/components/site/PublicPage.vue";
import { registerUrl } from "@/content/site";

const steps = [
	{ title: "Sign in with your mobile number", body: "Create an account or sign in with your mobile number and a one time password. New accounts complete a quick identity check.", safe: "OTP sign in and identity checks" },
	{ title: "Choose what you are paying for", body: "Pick the purpose, such as rent, fees, a deposit or a contractor, and enter the amount you need to pay.", safe: "Payments are tied to a real purpose" },
	{ title: "Add the recipient's bank details", body: "Enter the account number, IFSC code and exact account name. Save a recipient once and pay them again in a few taps.", safe: "Details are shown back to you to check" },
	{ title: "Review, then pay with your card", body: "See the amount, the applicable charges and the total before you pay. Your bank then asks you to approve the payment, usually with an OTP.", safe: "Charges shown upfront, approved by your bank" },
	{ title: "Get your receipt and track it", body: "You get a confirmation and a transaction reference straight away. Follow the status until the money settles into the recipient's account.", safe: "A receipt and reference for every payment" }
];

// Illustrative sample data for the preview screens (fictional recipient, mirrors paydeck-ui samples)
const sampleRecipient = { beneficiary_id: "b1", bank_account_name: "Example Home Properties", bank_name: "HDFC Bank", bank_branch_ifsc: "HDFC0000123", bank_account_number: "50100023452048", rant_id: "R1" };
const recipientItems = [
	{ label: "Account name", value: "Example Home Properties", mono: false },
	{ label: "IFSC", value: "HDFC0000123" }
];
const reviewItems = [
	{ label: "Rent", value: formatINR(25000, { whole: true }), strong: true },
	{ label: "Charges", value: "Shown before you pay", mono: false }
];
const receiptItems = [
	{ label: "Purpose", value: "Rent", mono: false },
	{ label: "Reference", value: "TD1789201001001" },
	{ label: "Settlement UTR", value: "HDFCN52026091200123" }
];

const pathNodes = [
	{ title: "You", check: "Sign in with OTP", icon: Smartphone },
	{ title: "Paydeck", check: "Identity and purpose checks", icon: ShieldCheck },
	{ title: "Payment gateway", check: "Card details handled securely", icon: Waypoints },
	{ title: "Your card's bank", check: "Approves with OTP", icon: CreditCard },
	{ title: "Recipient's bank", check: "Paid to their account", icon: Landmark }
];

const safety = [
	{ title: "Your bank approves every payment", body: "Card payments are authenticated by the bank that issued your card, usually with an OTP, before anything goes through.", when: "During payment", icon: BadgeCheck, tone: "orange", span: "span-5" },
	{ title: "We do not store your full card details", body: "Card information is handled by secure banking and payment partners, not kept by Paydeck.", when: "During payment", icon: CreditCard, tone: "ink", span: "span-7" },
	{ title: "Charges shown before you pay", body: "The amount, the applicable charges and the total are on screen before you confirm. No surprises later.", when: "Before payment", icon: Eye, tone: "white", span: "span-4" },
	{ title: "Identity checks on accounts", body: "Accounts complete identity checks, so payments come from real, verified people.", when: "Before payment", icon: UserCheck, tone: "white", span: "span-4" },
	{ title: "A record you can return to", body: "Every payment gets a confirmation, a transaction reference and a settlement status you can track.", when: "After payment", icon: ReceiptText, tone: "white", span: "span-4" },
	{ title: "A safety window for fraud reports", body: "A payment reported as fraud before it clears can be stopped. A Monday payment reported by 11 AM on Tuesday, for example, can still be held.", when: "After payment", icon: ShieldAlert, tone: "peach", span: "span-7" },
	{ title: "Policies in plain view", body: "Our terms, privacy, refund and fraud policies are public and easy to read.", when: "Always", icon: FileCheck2, tone: "cream", span: "span-5" }
];
</script>
